// ligfrontend/src/components/Lig/LigFeedComponent.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { notesAPI } from "../../services/api";
import LoadingSpinner from "../common/LoadingSpinner";
import FeedNote from "./FeedNote";
import "./LigFeedComponent.css";

const LigFeedComponent = () => {
  const { year } = useParams();
  const navigate = useNavigate();
  const observerRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadNotes();
  }, [year]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await notesAPI.getNotesFamily(year);

      setNotes(data.notes || []);
      buildDayWrapper(data.notes || [], []);
    } catch (error) {
      console.error("Error loading notes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreNotes = useCallback(async () => {
    if (loadingMore || !hasMore || notes.length === 0) return;

    try {
      setLoadingMore(true);
      const lastNote = notes[notes.length - 1];
      const data = await notesAPI.getMoreNotesFamily(lastNote.date);

      if (data.notes && data.notes.length > 0) {
        setNotes((prev) => [...prev, ...data.notes]);
        buildDayWrapper(data.notes, days);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error loading more notes:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [notes, loadingMore, hasMore, days]);

  const buildDayWrapper = (newNotes, existingDays) => {
    let prevDay = null;
    let updatedDays = [...existingDays];

    if (updatedDays.length > 0 && notes.length > 0) {
      const lastDayNotes = updatedDays[updatedDays.length - 1].notes;
      const lastNote = lastDayNotes[lastDayNotes.length - 1];
      prevDay = new Date(lastNote.date);
    }

    for (let i = 0; i < newNotes.length; i++) {
      const note = newNotes[i];

      // Note already has member data from backend
      const exNote = {
        note,
        member: note.member,
        revealName: false,
      };

      const noteDate = new Date(note.date);
      noteDate.setHours(0, 0, 0, 0);

      if (
        prevDay !== null &&
        prevDay.getDate() === noteDate.getDate() &&
        prevDay.getMonth() === noteDate.getMonth()
      ) {
        const lastDay = updatedDays[updatedDays.length - 1];
        lastDay.notes.push(exNote);
      } else {
        const dayWrapper = {
          date: noteDate,
          notes: [exNote],
        };
        updatedDays.push(dayWrapper);
      }

      prevDay = noteDate;
    }

    setDays(updatedDays);
  };

  const handleRevealName = (dayIndex, noteIndex) => {
    setDays((prevDays) => {
      const newDays = [...prevDays];
      newDays[dayIndex].notes[noteIndex].revealName = true;
      return newDays;
    });
  };

  // Intersection Observer for infinite scroll
  const lastNoteRef = useCallback(
    (node) => {
      if (loadingMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreNotes();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [loadingMore, hasMore, loadMoreNotes]
  );

  if (loading) {
    return <LoadingSpinner message={`Loading ${year} memories...`} />;
  }

  return (
    <div className="ligfeed-container">
      <div className="ligfeed-header">
        <button className="back-button" onClick={() => navigate("/years")}>
          ← All Years
        </button>
        <h1 className="ligfeed-year">{year}</h1>
        <p className="ligfeed-subtitle">
          {days.length} {days.length === 1 ? "day" : "days"} of memories
        </p>
      </div>

      <div className="feed-timeline">
        {days.map((day, dayIndex) => (
          <DayCard
            key={`${day.date}-${dayIndex}`}
            day={day}
            dayIndex={dayIndex}
            onRevealName={handleRevealName}
            isLast={dayIndex === days.length - 1}
            lastNoteRef={lastNoteRef}
          />
        ))}

        {loadingMore && (
          <div className="loading-more">
            <div className="spinner-small"></div>
            <span>Loading more memories...</span>
          </div>
        )}

        {days.length === 0 && (
          <div className="empty-feed">
            <div className="empty-feed-icon">📝</div>
            <h3>No Memories for {year}</h3>
            <p>No notes were written this year.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const DayCard = ({ day, dayIndex, onRevealName, isLast, lastNoteRef }) => {
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <div className="day-card">
      <div className="day-header">
        <div className="day-date">{formatDate(day.date)}</div>
        <div className="day-line"></div>
      </div>

      <div className="day-notes">
        {day.notes.map((noteEx, noteIndex) => {
          const isLastNote = isLast && noteIndex === day.notes.length - 1;

          return (
            <div key={noteEx.note._id} ref={isLastNote ? lastNoteRef : null}>
              <FeedNote
                noteEx={noteEx}
                time={formatTime(noteEx.note.date)}
                onReveal={() => onRevealName(dayIndex, noteIndex)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LigFeedComponent;
